import React from 'react';
import GoalStatusBadge, { GoalStatus } from './GoalStatusBadge';
import GoalOutcomeBadge from './GoalOutcomeBadge';
import GoalMilestoneBadge from './GoalMilestoneBadge';
import GoalProgressBar from './GoalProgressBar';
import GoalMetadata from './GoalMetadata';
import GoalUpdateHistory from './GoalUpdateHistory';

import { Goal as GoalType } from '../../../store/slices/goalsSlice';

export type Goal = GoalType;

interface GoalCardProps {
  goal: Goal;
  showActions?: boolean;
  actions?: React.ReactNode;
  onValueChange?: (newValue: number) => void;
  className?: string;
}

const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  showActions = false,
  actions,
  onValueChange,
  className = '',
}) => {
  return (
    <div className={`border rounded-lg p-4 bg-white hover:shadow-md transition-shadow ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="font-semibold text-gray-900">{goal.title}</h4>
            <GoalStatusBadge status={goal.status} />
            <GoalOutcomeBadge outcome={goal.outcome} />
          </div>
          {goal.description && (
            <p className="text-sm text-gray-600 mb-3">{goal.description}</p>
          )}
          {(() => {
            // Handle milestoneId being either a string (ID) or object (populated)
            const milestone = typeof goal.milestoneId === 'object' && goal.milestoneId !== null
              ? goal.milestoneId
              : goal.milestone;
            
            if (milestone && typeof milestone === 'object') {
              return (
                <div className="mb-3">
                  <GoalMilestoneBadge milestone={milestone} />
                </div>
              );
            }
            return null;
          })()}
          <GoalProgressBar
            currentValue={goal.currentValue}
            targetValue={goal.targetValue}
            unit={goal.unit}
            progress={goal.progress}
            className="mb-3"
          />
          <GoalMetadata
            status={goal.status}
            delayReason={goal.delayReason}
            postponeReason={goal.postponeReason}
            cancelReason={goal.cancelReason}
            conclusion={goal.conclusion}
            results={goal.results}
            outcomeNotes={goal.outcomeNotes}
          />
          {goal.updates && goal.updates.length > 0 && (
            <GoalUpdateHistory updates={goal.updates} unit={goal.unit} />
          )}
        </div>
        {showActions && actions && (
          <div className="flex gap-2 ml-4">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalCard;

